/**
 * Computes the redirect path after successful login (same logic as useAuthRedirect).
 * Used in the login thunk so we can navigate immediately after unwrap() without waiting for re-render.
 * Onboarding wizard, create-org flow, and cleaning standards are always part of the flow (no feature flags).
 */

import ROUTES from '../constants/routes';
import { USER_ROLES } from '../constants/userRoles';
import { isOnboardingStepCompleted } from './onboardingStepsUtils';

const CLEANING_STANDARDS_ENABLED = true;

function hasNoOrganization(orgId) {
  return orgId == null || orgId === '';
}

function isProfileIncomplete(user) {
  const fullName = user?.fullName;
  const phoneNumber = user?.phoneNumber;
  return fullName == null || fullName === '' || phoneNumber == null || phoneNumber === '';
}

function isOnboardingIncomplete(steps, requireStep3 = true) {
  if (!steps) return false;
  const step1Done = isOnboardingStepCompleted(steps, 'step1');
  const step3Done = isOnboardingStepCompleted(steps, 'step3');
  return !step1Done || (requireStep3 && !step3Done);
}

function isOrgAdminOrPrimaryAdmin(role) {
  return role === USER_ROLES.PRIMARY_ADMIN || role === USER_ROLES.ORGANIZATION_ADMIN;
}

/**
 * @param {{ user: object, onboardingData: object | null }} payload - login.fulfilled payload
 * @returns {string} path to navigate to
 */
export function getRedirectPathAfterLogin(payload) {
  const user = payload?.user ?? payload;
  const onboardingData = payload?.onboardingData ?? null;
  const orgId = user?.organization ?? user?.organizationId;
  const userRole = user?.role;
  const steps = onboardingData?.steps ?? null;

  if (hasNoOrganization(orgId)) return `${ROUTES.CREATE_ORG}?step=1`;

  if (orgId && steps && isOnboardingIncomplete(steps, CLEANING_STANDARDS_ENABLED)) {
    return ROUTES.ORGANIZATION_CONFIGURATION;
  }

  if (isProfileIncomplete(user)) return ROUTES.COMPLETE_PROFILE;

  if (user?.needsInitialSetup === true) return ROUTES.FACILITY_SELECTION;
  if (!isOrgAdminOrPrimaryAdmin(userRole)) return ROUTES.FACILITY_SELECTION;
  if (userRole === USER_ROLES.ORGANIZATION_ADMIN) return ROUTES.FACILITY_SELECTION;
  if (!steps) return ROUTES.FACILITY_SELECTION;

  const step1Done = isOnboardingStepCompleted(steps, 'step1');
  const step3Done = isOnboardingStepCompleted(steps, 'step3');
  const complete = CLEANING_STANDARDS_ENABLED ? step1Done && step3Done : step1Done;
  return complete ? ROUTES.DASHBOARD : ROUTES.ORGANIZATION_CONFIGURATION;
}
